import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, User, Share2 } from 'lucide-react';
import { getBlogPost } from '@/lib/api';
import { toast } from 'sonner';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const data = await getBlogPost(slug);
        setPost(data);
      } catch (error) {
        console.error('Error fetching post:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url,
        });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="h-4 bg-white/5 w-1/4 mb-4 img-loading" />
          <div className="h-12 bg-white/5 w-3/4 mb-8 img-loading" />
          <div className="h-64 bg-white/5 img-loading" />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen pt-32 pb-24 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">Post not found</p>
          <Link to="/blog" className="text-gold hover:text-gold/80 transition-colors">
            Return to Journal
          </Link>
        </div>
      </div>
    );
  }

  // Simple markdown-like rendering for paragraphs
  const renderContent = (content) => {
    return content.split('\n\n').map((paragraph, index) => {
      if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
        return (
          <h3 key={index} className="text-xl font-semibold mt-8 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            {paragraph.replace(/\*\*/g, '')}
          </h3>
        );
      }
      if (paragraph.startsWith('- ')) {
        const items = paragraph.split('\n').map(item => item.replace('- ', ''));
        return (
          <ul key={index} className="list-disc list-inside space-y-2 my-4 text-white/70">
            {items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        );
      }
      return (
        <p key={index} className="text-white/70 leading-relaxed mb-6">
          {paragraph}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="blog-post-page">
      <article className="max-w-3xl mx-auto px-6 lg:px-12">
        {/* Back Link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors mb-12"
          data-testid="back-to-blog"
        >
          <ArrowLeft size={16} />
          <span className="text-sm uppercase tracking-widest">Back to Journal</span>
        </Link>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <div className="flex flex-wrap items-center gap-4 mb-6">
            {post.tags?.map((tag) => (
              <span key={tag} className="mono text-xs text-gold uppercase">
                {tag}
              </span>
            ))}
          </div>

          <h1
            className="text-3xl lg:text-4xl xl:text-5xl font-semibold mb-8 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="post-title"
          >
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-white/40 text-sm">
            <div className="flex items-center gap-2">
              <User size={14} />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={14} />
              <span className="mono">{formatDate(post.published_at)}</span>
            </div>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 hover:text-gold transition-colors"
              data-testid="share-btn"
            >
              <Share2 size={14} />
              <span>Share</span>
            </button>
          </div>
        </motion.header>

        {/* Cover Image */}
        {post.cover_image && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-12"
          >
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full aspect-[16/9] object-cover"
            />
          </motion.div>
        )}

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="prose"
          data-testid="post-content"
        >
          {renderContent(post.content)}
        </motion.div>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-16 pt-8 border-t border-white/10"
        >
          <div className="flex items-center justify-between">
            <Link
              to="/blog"
              className="text-white/50 hover:text-white transition-colors text-sm uppercase tracking-widest"
            >
              ← More Posts
            </Link>
            <button
              onClick={handleShare}
              className="text-gold hover:text-gold/80 transition-colors text-sm uppercase tracking-widest flex items-center gap-2"
            >
              Share This Post
              <Share2 size={14} />
            </button>
          </div>
        </motion.footer>
      </article>
    </div>
  );
};

export default BlogPostPage;
